/**
 * DomainAddModal — minimal "Domein toevoegen" form for a tenant.
 *
 * POSTs to /tenants/{slug}/domains (DomainCreateIn). Only `fqdn` is required;
 * `organizational_domain` is derived server-side when omitted and the desired
 * DMARC policy is optional. Mirrors TenantCreateModal's shape/behaviour.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { ApiError } from '@/lib/api';
import { apiPost } from '@/features/_shared/queries';
import { toast } from '@/lib/toast';

interface Props {
  open: boolean;
  onClose: () => void;
  slug: string;
}

interface DomainCreated {
  id: string;
  fqdn: string;
}

type DesiredPolicy = '' | 'none' | 'quarantine' | 'reject';

// Light client-side guard; the backend (FqdnStr) is the authority.
const FQDN_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

export function DomainAddModal({ open, onClose, slug }: Props): JSX.Element | null {
  const qc = useQueryClient();
  const [fqdn, setFqdn] = useState('');
  const [desiredPolicy, setDesiredPolicy] = useState<DesiredPolicy>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setFqdn('');
      setDesiredPolicy('');
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      apiPost<DomainCreated>(
        `/tenants/${slug}/domains`,
        {
          fqdn,
          desired_policy: desiredPolicy || null,
        },
        { tenant: slug },
      ),
    onSuccess: (created) => {
      toast({ tone: 'success', message: `Domein '${created.fqdn}' toegevoegd.` });
      void qc.invalidateQueries({ queryKey: ['domains', slug] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.problem.title ?? 'Toevoegen mislukt.');
      } else {
        setError('Onbekende fout bij toevoegen.');
      }
    },
  });

  if (!open) return null;

  const fqdnValid = FQDN_RE.test(fqdn.trim().toLowerCase());
  const canSubmit = !mutation.isPending && fqdnValid;

  const handleSubmit = (evt: FormEvent<HTMLFormElement>): void => {
    evt.preventDefault();
    setError(null);
    if (!canSubmit) return;
    mutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-label="Domein toevoegen"
      onClick={onClose}
    >
      <div
        className="w-[440px] max-w-[95vw] rounded-md bg-white shadow-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-[14px] font-semibold text-slate-900">Domein toevoegen</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="text-slate-400 hover:text-slate-900"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-4 text-[13px]">
          <div>
            <label className="mb-1 block font-medium text-slate-700" htmlFor="domain-fqdn">
              Domein (FQDN)
            </label>
            <input
              id="domain-fqdn"
              type="text"
              value={fqdn}
              onChange={(e) => setFqdn(e.target.value.toLowerCase())}
              placeholder="example.com"
              required
              autoFocus
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 font-mono text-[12px] outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Volledige domeinnaam, kleine letters. Het organisatie-domein wordt automatisch
              afgeleid.
            </p>
          </div>

          <div>
            <label className="mb-1 block font-medium text-slate-700" htmlFor="domain-policy">
              Gewenste DMARC-policy <span className="font-normal text-slate-400">(optioneel)</span>
            </label>
            <select
              id="domain-policy"
              value={desiredPolicy}
              onChange={(e) => setDesiredPolicy(e.target.value as DesiredPolicy)}
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
            >
              <option value="">— niet ingesteld —</option>
              <option value="none">none</option>
              <option value="quarantine">quarantine</option>
              <option value="reject">reject</option>
            </select>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded border border-fail-100 bg-fail-50 px-2.5 py-1.5 text-[12px] text-fail-700"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 hover:bg-slate-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded bg-brand-600 px-3 py-1.5 font-medium text-slate-900 hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? 'Bezig…' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
